import React, { useEffect, useState } from 'react';
import { getBatchInferenceHistory, getBatchInferencePresignedUrl, getExportedModels } from '../services/api';
import '../styles/BatchInferenceHistory.css';

const BatchInferenceHistory = () => {
  const [searchMode, setSearchMode] = useState('uid'); // 'uid' or 'model'
  const [uidSearch, setUidSearch] = useState('');
  const [modelSearch, setModelSearch] = useState({ modelName: '', modelVersion: '' });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [downloadingStates, setDownloadingStates] = useState({});
  const [hasSearched, setHasSearched] = useState(false);
  const [exportedModels, setExportedModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(true);

  const token = localStorage.getItem('adminToken');

  // Fetch exported models on mount
  useEffect(() => {
    const fetchExportedModels = async () => {
      try {
        const response = await getExportedModels(token);
        if (response.data && response.data.data) {
          setExportedModels(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching exported models:', error);
      } finally {
        setLoadingModels(false);
      }
    };

    if (token) {
      fetchExportedModels();
    }
  }, [token]);

  const handleSearch = async () => {
    if (!token) return;

    setLoading(true);
    setHasSearched(true);
    try {
      let params = {};

      if (searchMode === 'uid') {
        if (!uidSearch.trim()) {
          alert('Please enter a UID');
          setLoading(false);
          return;
        }
        params = { uid: uidSearch };
      } else {
        if (!modelSearch.modelName.trim() || !modelSearch.modelVersion.trim()) {
          alert('Please enter both model name and version');
          setLoading(false);
          return;
        }
        params = {
          model_name: modelSearch.modelName,
          model_version: modelSearch.modelVersion,
          page: 1,
          page_size: pageSize
        };
      }

      const response = await getBatchInferenceHistory(token, params);
      const data = response.data;

      if (data.success) {
        if (searchMode === 'uid') {
          setResults(data.data ? [data.data] : []);
          setTotalRecords(data.data ? 1 : 0);
        } else {
          setResults(data.data || []);
          setTotalRecords(data.total || 0);
          setCurrentPage(data.page || 1);
        }
      } else {
        alert(data.message || 'Failed to fetch history');
        setResults([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error('Error fetching batch inference history:', error);
      alert('Error: ' + (error.response?.data?.message || error.message));
      setResults([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    handleSearch();
  };

  const handleDownload = async (s3Path, fileName) => {
    if (!s3Path) {
      alert('File path not available');
      return;
    }

    const downloadKey = `${s3Path}`;
    setDownloadingStates(prev => ({ ...prev, [downloadKey]: true }));

    try {
      const response = await getBatchInferencePresignedUrl(token, s3Path);
      const data = response.data;

      if (data.success && data.presigned_url) {
        const link = document.createElement('a');
        link.href = data.presigned_url;
        link.download = fileName || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert(data.message || 'Failed to generate download link');
      }
    } catch (error) {
      console.error('Error generating presigned URL:', error);
      alert('Error: ' + (error.response?.data?.message || error.message));
    } finally {
      setDownloadingStates(prev => ({ ...prev, [downloadKey]: false }));
    }
  };

  const getFileName = (s3Path) => {
    if (!s3Path) return 'download';
    const parts = s3Path.split('/');
    return parts[parts.length - 1] || 'download';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const totalPages = Math.ceil(totalRecords / pageSize);

  return (
    <div className="bih-wrapper">
      <div className="bih-header">
        <h2>Batch Inference History</h2>
        <p>Search and download batch inference results</p>
      </div>

      <div className="bih-filter-section">
        <div className="bih-search-mode">
          <button
            className={`bih-mode-btn ${searchMode === 'uid' ? 'active' : ''}`}
            onClick={() => {
              setSearchMode('uid');
              setResults([]);
              setHasSearched(false);
              setUidSearch('');
            }}
          >
            Search by UID
          </button>
          <button
            className={`bih-mode-btn ${searchMode === 'model' ? 'active' : ''}`}
            onClick={() => {
              setSearchMode('model');
              setResults([]);
              setHasSearched(false);
              setModelSearch({ modelName: '', modelVersion: '' });
            }}
          >
            Search by Model
          </button>
        </div>

        {searchMode === 'uid' ? (
          <div className="bih-search-form">
            <input
              type="text"
              placeholder="Enter UID (e.g., a1b2c3d4)"
              value={uidSearch}
              onChange={(e) => setUidSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button onClick={handleSearch} disabled={loading} className="bih-search-btn">
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        ) : (
          <div className="bih-search-form">
            <select
              value={modelSearch.modelName}
              onChange={(e) =>
                setModelSearch({ ...modelSearch, modelName: e.target.value, modelVersion: '' })
              }
              className="bih-select"
              disabled={loadingModels}
            >
              <option value="">-- Select Model Name --</option>
              {exportedModels && [...new Set(exportedModels.map(m => m.name))].map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            <select
              value={modelSearch.modelVersion}
              onChange={(e) =>
                setModelSearch({ ...modelSearch, modelVersion: e.target.value })
              }
              className="bih-select"
              disabled={!modelSearch.modelName || loadingModels}
            >
              <option value="">-- Select Version --</option>
              {modelSearch.modelName && exportedModels
                .filter(m => m.name === modelSearch.modelName)
                .map(m => (
                  <option key={`${m.name}-${m.version}`} value={m.version}>
                    v{m.version}
                  </option>
                ))}
            </select>
            <button onClick={handleSearch} disabled={loading || loadingModels} className="bih-search-btn">
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        )}
      </div>

      {hasSearched && (
        <div className="bih-results-section">
          {loading && <div className="bih-loading">Loading...</div>}

          {!loading && results.length === 0 && (
            <div className="bih-no-results">No results found</div>
          )}

          {!loading && results.length > 0 && (
            <>
              <div className="bih-table-wrapper">
                <table className="bih-table">
                  <thead>
                    <tr>
                      <th>UID</th>
                      <th>Model</th>
                      <th>Version</th>
                      <th>Submitted By</th>
                      <th>Submitted At</th>
                      <th>Input Data</th>
                      <th>Downloads</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((record) => (
                      <tr key={record.uid}>
                        <td className="bih-uid">{record.uid}</td>
                        <td>{record.model_name}</td>
                        <td>{record.model_version}</td>
                        <td>{record.submitted_by || '-'}</td>
                        <td>{formatDate(record.submitted_at)}</td>
                        <td>
                          <button
                            className="bih-download-btn"
                            onClick={() =>
                              handleDownload(
                                record.input_data_path,
                                `training_data_${record.uid}.csv`
                              )
                            }
                            disabled={
                              !record.input_data_path ||
                              downloadingStates[record.input_data_path]
                            }
                            title={record.input_data_path ? 'Training Data' : 'N/A'}
                          >
                            📊 Data
                          </button>

                        </td>
                        <td className="bih-downloads">
                          <button
                            className="bih-download-btn"
                            onClick={() =>
                              handleDownload(
                                record.predictions_path,
                                `predictions_${record.uid}.csv`
                              )
                            }
                            disabled={
                              !record.predictions_path ||
                              downloadingStates[record.predictions_path]
                            }
                            title={record.predictions_path ? 'Download Predictions' : 'N/A'}
                          >
                            📊 Pred
                          </button>
                          <button
                            className="bih-download-btn"
                            onClick={() =>
                              handleDownload(
                                record.lime_shap_path,
                                `lime_shap_${record.uid}.html`
                              )
                            }
                            disabled={
                              !record.lime_shap_path ||
                              downloadingStates[record.lime_shap_path]
                            }
                            title={
                              record.lime_shap_path ? 'Download LIME/SHAP' : 'Not available'
                            }
                          >
                            🔍 LIME
                          </button>
                          <button
                            className="bih-download-btn"
                            onClick={() =>
                              handleDownload(record.drift_path, `drift_${record.uid}.zip`)
                            }
                            disabled={
                              !record.drift_path || downloadingStates[record.drift_path]
                            }
                            title={record.drift_path ? 'Download Drift Report' : 'Not available'}
                          >
                            📈 Drift
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {searchMode === 'model' && totalPages > 1 && (
                <div className="bih-pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    ← Previous
                  </button>
                  <span className="bih-page-info">
                    Page {currentPage} of {totalPages} ({totalRecords} total)
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BatchInferenceHistory;
